// The `evidence/leaderboard` per-user usage aggregate — carved from
// `src/hooks/query/useQueryEvidenceUsageStats.ts` (now a bodied delegate).
//
// TWO SEVERANCES: S1 (the module read `@/utils/scribeHTTP#scribeHttpV2`
// DIRECTLY, with the request inline in its `queryFn` — that transport now lives
// in `../api/evidence-leaderboard` over the registry) and S2 (the `token &&
// enabled` gate, collapsed to the one boolean the delegate computes).
//
// THE GATE IS `skipToken`, NOT `enabled`, AND THE DIFFERENCE IS BEHAVIOUR.
// A disabled query reports `isPending` forever; a `skipToken` query reports
// `isPending` too but can never be triggered by a refetch, and the pre-move hook
// chose it deliberately for a read whose consumer — the trial-recap surface —
// branches on `isPending` to decide whether to draw at all. Rewriting this as
// `enabled` would compile, look tidier and change which state the recap sees on
// a signed-out render.
//
// THE ROUTE AND THE CACHE IDENTITY DISAGREE, and the family claim rests on the
// rule the sibling carve ratified: `/user-activity/evidence` is a user-activity
// route, while the identity is `evidenceUsageStats`. Where route and identity
// agree the route decides; where they disagree the identity does, because the
// identity is what the frozen-key law protects. Recorded on the family record
// rather than settled quietly here.
import { skipToken, useQuery } from '@tanstack/react-query';

import {
  EVIDENCE_USAGE_STATS_QUERY_KEY,
  type EvidenceUsageStatsResponse,
} from '../../contracts';
import { getEvidenceUsageStats } from '../api/userActivity';

/**
 * GET /api/v2/ml-scribe/user-activity/evidence
 *
 * Per-user evidence usage stats for surfaces such as the 90-day trial recap modal.
 *
 * @param enabled - App-resolved gate (`!!token && <caller's own enabled>`),
 *   already reduced to one decision by the delegate.
 */
export function useQueryEvidenceUsageStats(enabled: boolean) {
  return useQuery({
    queryKey: [EVIDENCE_USAGE_STATS_QUERY_KEY],
    queryFn: enabled
      ? async (): Promise<EvidenceUsageStatsResponse> => getEvidenceUsageStats()
      : skipToken,
    staleTime: 60 * 1000,
  });
}
